import React from 'react';
import classnames from 'classnames';
import Dropzone from 'react-dropzone';

import Modal from 'ub/components/modal';
import Toolbar from './toolbar';
import AssetGrid from './asset-grid';
import UploadButton from './upload-button';
import DeleteConfirmation from './delete-confirmation';

import {
  itemsPropType,
  assetSourcePropType,
  sortByPropType,
  assetTypePropType,
} from './prop-types';

import styles from './asset-library.scss';

export default class AssetLibrary extends React.PureComponent {
  constructor(props) {
    super(props);

    this.handleChooseItem = this.handleChooseItem.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  handleChooseItem() {
    this.props.dispatch({ type: 'choose' });
  }

  handleClose() {
    this.props.dispatch({ type: 'close' });
  }

  render() {
    const {
      allowChoose,
      allowDelete,
      allowSort,
      allowUpload,
      chooseButtonText,
      dispatch,
      emptyMessage,
      failedUploads,
      fetchError,
      isConfirmingDelete,
      isLoading,
      items,
      largeThumbnails,
      onScrollNearBottom,
      onSelectFilesForUpload,
      searchTerm,
      selectedItemIds,
      selectedSource,
      selectedType,
      sortAscending,
      sortBy,
      title,
      typeTotals,
      uploadButtonText,
      uploadEndpoint,
      uploadingText,
      uploadsInProgress,
      visibleTypes,
    } = this.props;

    const onlyImages = visibleTypes.length === 1 && visibleTypes[0] === 'images';

    return (
      <Dropzone
        disableClick
        disablePreview
        activeClassName={styles.dropReady}
        style={{}} // this is required to remove default styles
        onDrop={onSelectFilesForUpload}
      >
        <Modal
          closePortal={this.handleClose}
          title={title}
          className={classnames('modal-panel', 'responsive-panel', 'asset-library', styles.modal)}
        >
          <div className="content-panel modal-content-panel">
            <div
              className={classnames('tab-view', 'asset-browser', 'modal-asset-browser', 'grid', {
                loading: isLoading,
              })}
            >
              <div className={classnames('view-pane', styles.viewPane, 'options-panel-view-pane')}>
                <div className={classnames(styles.assetView)}>
                  <Toolbar
                    allowDelete={allowDelete && selectedItemIds.length > 0}
                    dispatch={dispatch}
                    searchTerm={searchTerm}
                    selectedSource={selectedSource}
                    selectedType={selectedType}
                    sortAscending={sortAscending}
                    sortBy={sortBy}
                    sortVisible={allowSort}
                    sourceDropdownVisible={onlyImages}
                    typeTotals={typeTotals}
                    visibleTypes={isLoading ? [] : visibleTypes}
                  />

                  <AssetGrid
                    allowDelete={allowDelete}
                    allowUpload={allowUpload}
                    dispatch={dispatch}
                    emptyMessage={emptyMessage}
                    fetchError={fetchError}
                    isEmpty={typeTotals[selectedType] === 0 && !isLoading && !searchTerm}
                    items={items}
                    largeThumbnails={largeThumbnails}
                    onScrollNearBottom={onScrollNearBottom}
                    selectedItemIds={selectedItemIds}
                    selectedSource={selectedSource}
                    sortAscending={sortAscending}
                    sortBy={sortBy}
                  />
                </div>
              </div>

              <div className={classnames('modal-sheet', 'loading-overlay', styles.loadingOverlay)}>
                <div className="content">
                  <div className="loading-container">
                    <div className="loading-icon" />
                    <div className="loading-title">Loading...</div>
                  </div>
                </div>
              </div>

              {isConfirmingDelete && (
                <DeleteConfirmation
                  items={selectedItemIds
                    .map(itemId => items.find(item => item.id === itemId))
                    .filter(Boolean)}
                  dispatch={dispatch}
                />
              )}
            </div>
          </div>

          <div className="action-bar modal-action-panel">
            <a
              className={classnames(
                'button',
                'action',
                'js-asset-upload-choose-btn',
                'modal-choose-action-button',
                'modal-button',
                {
                  dsbl: !allowChoose,
                }
              )}
              onClick={allowChoose ? this.handleChooseItem : undefined}
            >
              <span className="label">{chooseButtonText}</span>
            </a>

            <a
              className="button js-asset-upload-cancel-btn push-left modal-cancel-action-button modal-button"
              onClick={this.handleClose}
            >
              <span className="label">Cancel</span>
            </a>

            {allowUpload ? (
              <UploadButton
                endpoint={uploadEndpoint}
                label={uploadButtonText}
                onlyImages={onlyImages}
                onSelectFiles={this.props.onSelectFilesForUpload}
              />
            ) : (
              <div />
            )}
          </div>

          {uploadsInProgress > 0 && (
            <div
              className={classnames(
                'modal-sheet',
                'loading-overlay',
                'uploading-overlay',
                styles.uploadingOverlay
              )}
            >
              <div className="content" />
              <div className="loading-container">
                <div className="loading-icon" />
                <div className="loading-title">
                  {uploadingText} {uploadsInProgress} remaining
                </div>
                <div className="loading-message">Please wait, this might take a while.</div>
              </div>
            </div>
          )}

          {failedUploads.length > 0 && (
            <div className="error-dialog">
              <p>Sorry, there was an error uploading the following:</p>
              <p>{failedUploads.map(uploadStr => <li key={uploadStr}>{uploadStr}</li>)}</p>
            </div>
          )}
        </Modal>
      </Dropzone>
    );
  }
}

AssetLibrary.propTypes = {
  allowChoose: React.PropTypes.bool.isRequired,
  allowDelete: React.PropTypes.bool.isRequired,
  allowSort: React.PropTypes.bool.isRequired,
  allowUpload: React.PropTypes.bool.isRequired,
  chooseButtonText: React.PropTypes.string.isRequired,
  dispatch: React.PropTypes.func.isRequired,
  emptyMessage: React.PropTypes.string.isRequired,
  failedUploads: React.PropTypes.arrayOf(React.PropTypes.string).isRequired,
  fetchError: React.PropTypes.instanceOf(Error),
  isConfirmingDelete: React.PropTypes.bool.isRequired,
  isLoading: React.PropTypes.bool.isRequired,
  items: itemsPropType.isRequired,
  largeThumbnails: React.PropTypes.bool.isRequired,
  onScrollNearBottom: React.PropTypes.func.isRequired,
  onSelectFilesForUpload: React.PropTypes.func.isRequired,
  searchTerm: React.PropTypes.string.isRequired,
  selectedItemIds: React.PropTypes.arrayOf(React.PropTypes.string).isRequired,
  selectedSource: assetSourcePropType.isRequired,
  selectedType: assetTypePropType.isRequired,
  sortAscending: React.PropTypes.bool.isRequired,
  sortBy: sortByPropType.isRequired,
  title: React.PropTypes.string.isRequired,
  typeTotals: React.PropTypes.object.isRequired,
  uploadButtonText: React.PropTypes.string,
  uploadEndpoint: React.PropTypes.string,
  uploadingText: React.PropTypes.string.isRequired,
  uploadsInProgress: React.PropTypes.number.isRequired,
  visibleTypes: React.PropTypes.arrayOf(assetTypePropType).isRequired,
};



// WEBPACK FOOTER //
// ./src/components/asset-library/asset-library.jsx